import axios from "axios";
import { Configuration, IAssetProvider, IConfigurator } from "threngine";
import { CarModelConfig } from "../ModelLoad/ModelLoadController";
import { SceneConfigAppendMode, SceneConfigEditor } from "./SceneConfigEditor";
import { Scene, DoubleSide, PointLight } from "three";
import { getWhiteLabel } from "../../../whitelabel";

export interface IEnvOption {
  id:string;
  dimmedID?:string;
  name:string;
  audio?:string;
};

interface IStudioEvent {
  trunkLoading?:{
    boxes?:string;
    shopping?:string;
    travelling?:string;
    default?:string;
  };
  carpaint?:{
    extra?:{
      materials:string[];
    }
    defaults?:{
      material:string;
    };
  };
  environment?:{
    extra?:IEnvOption[];
    replace?:{oldID:string, new:IEnvOption}[];
    defaultID?:string;
  };
  geometry?:{
    bindToEnvIDs?:string[]
    visibilityGroup:string;
    objects?:string[]
  }[];

  // HACK: this flag is used to mark events that have been added internally,
  // and should not count when checking whether events are active or not.
  internal?:boolean;
};

export class EventController {
  public carModelConfig:CarModelConfig;
  public configurator:IConfigurator;
  
  private events:IStudioEvent[] = [];
  private initialized:boolean = false;
  private currentEnvironmentID:string = "";
  private xrActive:boolean = false;

  public get areAnyEventsActive():boolean { 
    return this.events.filter(
      e => !e.internal).length > 0;
  }

  public async initialize(previewEventName?:string):Promise<void> {
    this.events = await this.queryCurrentEvents(previewEventName);
    this.initialized = true;

    (window as any)["event"] = this;
  }

  public rewriteSceneConfig(
    config:Configuration,
    assetProvider:IAssetProvider
  ):Configuration {
    if (!this.initialized) {
      throw 'EventController: called rewriteSceneConfig() before initialized completed'
    }

    if (this.events.length == 0) {
      return config;
    }

    const editor = new SceneConfigEditor(config, assetProvider);
    const carConfig = this.carModelConfig;

    for (const event of this.events) {
      const {trunkLoading, carpaint} = event;

      if (trunkLoading) {
        if (trunkLoading.boxes) {
          editor.replaceVisibilityGroupByName(carConfig.trunkLoading.boxes.visibilityGroup, trunkLoading.boxes);
        }

        if (trunkLoading.shopping) {
          editor.replaceVisibilityGroupByName(carConfig.trunkLoading.shopping.visibilityGroup, trunkLoading.shopping);
        }

        if (trunkLoading.travelling) {
          editor.replaceVisibilityGroupByName(carConfig.trunkLoading.travelling.visibilityGroup, trunkLoading.travelling);
        }
      }

      if (carpaint) {
        const carpaintGroup = carConfig.groupings.carpaint;
        const {extra, defaults} = carpaint;

        if (extra) {
          for (const name of extra.materials) {
            if (defaults && defaults.material == name) {
              editor.addMaterialToMaterialGroupByName(carpaintGroup, name, SceneConfigAppendMode.Front);
            } else {
              editor.addMaterialToMaterialGroupByName(carpaintGroup, name, SceneConfigAppendMode.Back);
            }
          }
        }

        if (defaults) {
          editor.setMaterialGroupDefaultByName(carpaintGroup, defaults.material);
        }
      }
    }

    return editor.config;
  }

  public rewriteEnvironmentOptions(options:IEnvOption[]):IEnvOption[] {
    for (const event of this.events) {
      const {environment} = event;
      if (environment) {
        if (environment.extra) {
          options.push.apply(options, environment.extra);
        }

        if (environment.replace) {
          for (const replacement of environment.replace) {
            const option = options.find(o => o.id == replacement.oldID);
            if (option) {
              option.id = replacement.new.id;
              option.name = replacement.new.name;
            }
          }
        }

        if (environment.defaultID) {
          const option = options.find(o => o.id == environment.defaultID);
          if (option) {
            // Move environment to the start of the list to set it as default.
            options = options.filter(o => o != option);
            options.unshift(option);
          }
        }
      }
    }

    return options;
  }

  public async onEnvironmentChange(id:string):Promise<void> {
    this.currentEnvironmentID = id;

    for (const event of this.events) {
      if (event.geometry) {
        for (const geometry of event.geometry) {
          let visible = true;
          let vg = this.configurator.config.visibility_groups.find(vg => vg.name === geometry.visibilityGroup);

          if (!vg) {
            console.error(`EventController: cannot find visibility group: ${geometry.visibilityGroup}`);
            continue;
          }

          if (geometry.bindToEnvIDs) {
            visible = false;
            for (const envID of geometry.bindToEnvIDs) {
              visible = visible || (id == envID);
            }
          }

          if (this.xrActive) {
            visible = false;
          }

          await this.configurator.setVisible(vg.grouping_ids, visible);

          const scene:Scene = (<any>this.configurator)._scene;

          if (geometry.objects) {
            for (const objectName of geometry.objects) {
              const object = scene.getObjectByName(objectName);

              if (object) {
                object.visible = visible;
              }
            }
          }

          // Fix rendering of pine needles.
          // TODO: fix this properly in the shell.
          scene.traverse(c => {
            if (c.name == "Tree_#Pine_Needles") {
              (c as any).material.side = DoubleSide;
              (c as any).material.transparent = true;
            }
          })
        }
      }
    }
  }

  public onXRBegin():void {
    this.xrActive = true;
    this.onEnvironmentChange(this.currentEnvironmentID);
  }

  public onXRFinish():void {
    this.xrActive = false;
    this.onEnvironmentChange(this.currentEnvironmentID);
  }

  public getDefaultTrunkCargo():string|undefined {
    for (const event of this.events) {
      if (event.trunkLoading && event.trunkLoading.default) {
        return event.trunkLoading.default;
      }
    }

    return undefined;
  }

  private async queryCurrentEvents(previewEventName?:string):Promise<IStudioEvent[]> {
    const response = await axios.get(`https://projects.viscircle.com/vw/getValidEvent`);

    const names:string[] = response.data.data;

    if (previewEventName && names.indexOf(previewEventName) === -1) {
      names.push(previewEventName);
    }

    const events:IStudioEvent[] = [];

    for (const name of names) {
      try {
        const response = await axios.get(`${HASH}/events/${name}.json`);

        events.push(response.data);
      } catch (e) {
        console.error(`EventController: could not load event data: ${name}!!!`);
      }
    }

    if (getWhiteLabel().name == "ID. Studio" && !names.includes("soccer")) {
      events.push({
        "environment":{
          "extra":[
            {
              "id":"BG Volkswagen Arena Daytime",
              "dimmedID":"BG Volkswagen Arena Night",
              "name":"Stadium",
              "audio":"stadium.mp3"
            }
          ]
        },
        "geometry":[
          {
            "bindToEnvIDs": ["BG Volkswagen Arena Daytime"],
            "visibilityGroup":"Soccer Geometry"
          }
        ],
        "internal": true
      });
    }

    return events;
  }
}